import type {Order} from "../app/reportPage/reportPage.component";
import {ReportSetting, ReportSettingKeyboardType, ReportSettingScannerMode} from "../enums/report";

// Accessors for the per-field extended reporting settings of an order. Kept free of app service
// imports so they can be unit-tested without loading the whole report service graph.

export const isSettingEnabled = (check: ReportSetting, order: Order) => {
    if (order.enable_nested_reporting && order.nested_reporting_settings) {
        let setting = order.nested_reporting_settings;

        return Object.keys(setting).includes(check);
    } else if (order.enable_single_components && order.single_components_settings) {
        let setting = order.single_components_settings;

        return Object.keys(setting).includes(check);
    }
}

export const getSettingValue = (check: ReportSetting, order: Order): any => {
  if (order.enable_nested_reporting && order.nested_reporting_settings) {
    const setting = order.nested_reporting_settings as any;
    if (check in setting) {
      return setting[check];
    }
  }

  if (order.enable_single_components && order.single_components_settings) {
    const setting = order.single_components_settings as any;
    if (check in setting) {
      return setting[check];
    }
  }

  return null;
};

export const getKeyboardType = (check: ReportSetting, order: Order): string => {
  const value = getSettingValue(check, order);

  if (value === 'KEYBOARD_TYPE_NUMBER') {
    return ReportSettingKeyboardType.NUMBER;
  }

  return ReportSettingKeyboardType.TEXT;
};

export const getCustomName = (check: ReportSetting, order: Order): string | null => {
  const value = getSettingValue(check, order);

  if (value && value.trim() !== '') {
    return value;
  }

  return null;
};

export const getScannerMode = (check: ReportSetting, order: Order): ReportSettingScannerMode | null => {
  const value = getSettingValue(check, order);

  if (!value) return null;

  return Object.values(ReportSettingScannerMode).includes(value as ReportSettingScannerMode)
    ? (value as ReportSettingScannerMode)
    : null;
};

export const shouldShowScanner = (check: ReportSetting, order: Order): boolean => {
  const mode = getScannerMode(check, order);

  if (!mode || mode === ReportSettingScannerMode.SCANNER_MODE_GENERAL) {
    return order.is_enable_single_label_element_scan;
  }

  return mode === ReportSettingScannerMode.SCANNER_MODE_SCANNER ||
    mode === ReportSettingScannerMode.SCANNER_MODE_SKIP_PREFIX;
};

export const getCodePrefixes = (check: ReportSetting, order: Order): string[] | null => {
  const value = getSettingValue(check, order);

  if (value && value.length > 0) {
    return value;
  }

  return null;
};

// Form field -> [scanner mode setting, scan sound setting]. The extranet lets the sound be ticked
// only when the field's scanner mode offers scanning; the app applies the same rule.
export const SCAN_SOUND_FIELD_SETTINGS: Record<string, [ReportSetting, ReportSetting]> = {
    componentNumberText: [ReportSetting.COMPONENT_NUMBER_SCANNER_MODE, ReportSetting.COMPONENT_NUMBER_SCAN_SOUND],
    levNumber: [ReportSetting.LEV_NUMBER_SCANNER_MODE, ReportSetting.LEV_NUMBER_SCAN_SOUND],
    batchNumber: [ReportSetting.BATCH_NUMBER_SCANNER_MODE, ReportSetting.BATCH_NUMBER_SCAN_SOUND],
    serialNumber: [ReportSetting.SERIAL_NUMBER_SCANNER_MODE, ReportSetting.SERIAL_NUMBER_SCAN_SOUND],
    deliveryNote: [ReportSetting.DELIVERY_NOTE_SCANNER_MODE, ReportSetting.DELIVERY_NOTE_SCAN_SOUND],
    materialNumber: [ReportSetting.MATERIAL_NUMBER_SCANNER_MODE, ReportSetting.MATERIAL_NUMBER_SCAN_SOUND],
    measureResult1: [ReportSetting.MEASURE_RESULT_1_SCANNER_MODE, ReportSetting.MEASURE_RESULT_1_SCAN_SOUND],
    measureResult2: [ReportSetting.MEASURE_RESULT_2_SCANNER_MODE, ReportSetting.MEASURE_RESULT_2_SCAN_SOUND],
    measureResult3: [ReportSetting.MEASURE_RESULT_3_SCANNER_MODE, ReportSetting.MEASURE_RESULT_3_SCAN_SOUND],
    customField1: [ReportSetting.CUSTOM_FIELD_1_SCANNER_MODE, ReportSetting.CUSTOM_FIELD_1_SCAN_SOUND],
    customField2: [ReportSetting.CUSTOM_FIELD_2_SCANNER_MODE, ReportSetting.CUSTOM_FIELD_2_SCAN_SOUND],
    customField3: [ReportSetting.CUSTOM_FIELD_3_SCANNER_MODE, ReportSetting.CUSTOM_FIELD_3_SCAN_SOUND],
    customField4: [ReportSetting.CUSTOM_FIELD_4_SCANNER_MODE, ReportSetting.CUSTOM_FIELD_4_SCAN_SOUND],
};

export const isScanSoundEnabled = (field: string, order: Order): boolean => {
    const settings = SCAN_SOUND_FIELD_SETTINGS[field];
    if (!settings) {
        return false;
    }

    const [scannerModeSetting, scanSoundSetting] = settings;

    return !!shouldShowScanner(scannerModeSetting, order) && !!isSettingEnabled(scanSoundSetting, order);
};

// A component number is allowed unless nested reporting restricts it to a list it is not on.
export const isComponentNumberAllowed = (value: string | null | undefined, order: Order): boolean => {
    const allowed = order.enable_nested_reporting ? order.nested_reporting_components : undefined;

    return !value || !allowed || allowed.length === 0 || allowed.includes(value);
};
